/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The WORDS of an offer's pricing model (docs/plan-aanbodmodellen-2026-10-15.md,
 * "Weergave"): ONE module for every string a surface prints about a staffel or
 * a commission with a vastrecht per kWh, so the comparer row's icon, its panel,
 * the profile page, the compare pages and the aanbiedersoverzicht say the same
 * thing in the same words. What a model COSTS is src/lib/offerPricing.js; this
 * only says it.
 *
 *   describeOfferPricing   the compact description — inside the "Verdienmodel"
 *                          aandachtspunt of a staffel and a vastrecht offer
 *                          (src/lib/termsVerdict.ts), the compare table's note
 *                          under the commission, the line under the profile's
 *                          figure:
 *                          "25% tot 3.500 kWh · 22% tot 5.000 kWh · … · 14% daarboven",
 *                          "14% + € 0,0057 per kWh, samen min. € 34,99 en max.
 *                          € 99,99 per laadpaal per jaar"
 *   staffelLadderRows      a staffel as rows of a ladder ("tot 3.500 kWh | 25%"),
 *                          its band at a volume marked — the verdienmodel
 *                          aandachtspunt draws them (StaffelLadder)
 *   offerPricingTooltip    the amber icon's title: the description behind
 *                          "Afwijkend verdienmodel: …" (`table.deviating_model_*`)
 *   overviewCommissionValue / overviewModelTooltip
 *                          the overview card, which knows the model's kind only:
 *                          "10% (staffel)", "14% + vastrecht"
 *   offerBreakdownLines    the worked example's extra lines (the vastrecht, the
 *                          yearly cap or floor), signed so the lines add up
 *   offerBreakdownTitle    the chain behind the comparer's net figure — for a
 *                          flat offer the string the row always printed
 *
 * Which phrase says which part of a model (the bands up to the next bound,
 * the vastrecht's bound variant) is src/lib/offerPricingDescribe.js, the
 * language-neutral builder the admin and the server's review mails word in
 * Dutch too (formatOfferPricingPhrases is this module's side of it).
 *
 * Only the two volume-dependent models that came in 2026-10 have words here; a
 * flat commission, a subscription and a fixed rate keep the copy they had
 * (the row's own cell, the three `table.deviating_model_*` keys). Every text
 * is a key with `{placeholders}` (fillTemplate), in both baselines; every
 * number goes through src/lib/formatters.ts — a percentage as the row prints
 * it (formatCommissionPct), the vastrecht with four decimals, a yearly bound
 * with cents only when it has them. Client-side: it takes the caller's `t` and
 * language, and is pure (the bake and the hydrating client agree).
 */

import type { Language } from '../contexts/LanguageContext';
import type { OfferPricing, OfferPricingTier } from '../types';
import type { OfferEarningsBreakdown } from './earnings';
import {
  eurDecimals,
  formatCurrencyEUR,
  formatErePrice,
  formatInteger,
  formatNumber,
} from './formatters';
import { tierFor } from './offerPricing.js';
import {
  OFFER_PRICING_PHRASE_SEPARATOR,
  offerPricingPhrases,
  staffelLadderBands,
  type OfferPricingPhraseKey,
} from './offerPricingDescribe.js';
import { fillTemplate } from './templateVars.js';

/** `t` as the components receive it: a key path in, the active language's text out. */
export type Translate = (key: string) => string;

/**
 * The models with words of their own, by the name the overview and the icon
 * know them by: a staffel, a commission plus vastrecht, and that model without
 * a vastrecht — a commission with a yearly minimum and/or maximum ('capped').
 */
export type VolumeModelKind = 'tiered' | 'kwh_fee' | 'capped';

const NS = 'offer_pricing';

/**
 * A commission in the page's own number format, as the comparer row prints it:
 * "17,5" on /nl, "17.5" on /en, a whole percentage without decimals ("15",
 * never "15,0"), at most two decimals.
 */
export const formatCommissionPct = (pct: number, language: Language): string =>
  formatNumber(pct, language, { maximumFractionDigits: 2 });

/**
 * A € per kWh of a model (a vastrecht, a fixed rate) at its own precision:
 * always four decimals ("€ 0,0057") — what the editors print, so the
 * verdienmodel aandachtspunt names the same figure.
 */
export const formatKwhRate = (eur: number, language: Language): string =>
  formatCurrencyEUR(eur, language, 4);

/** A yearly bound or a line of the chain: cents only when it has them ("€ 34,99", "€ 100"). */
const formatBound = (eur: number, language: Language): string =>
  formatCurrencyEUR(eur, language, eurDecimals(eur));

/** A line of the printed chain: always two decimals ("€ 14,25"). */
const money2 = (eur: number, language: Language): string => formatCurrencyEUR(eur, language, 2);

/** The model kind of a pricing that has words here, or null. */
export function volumeModelKind(pricing: OfferPricing | null | undefined): VolumeModelKind | null {
  if (pricing?.model === 'tiered') return 'tiered';
  if (pricing?.model === 'commission_plus_kwh_fee') {
    return pricing.eurPerKwh === null ? 'capped' : 'kwh_fee';
  }
  return null;
}

/**
 * The model in one line, or null for a model without words here. A staffel
 * names each band's percentage up to the next band's lower bound and the last
 * one "daarboven"; a vastrecht offer names its percentage, the rate and the
 * yearly bounds it has (both, one, or none).
 */
export function describeOfferPricing(
  t: Translate,
  language: Language,
  pricing: OfferPricing | null | undefined
): string | null {
  if (volumeModelKind(pricing) === null) return null;
  return formatOfferPricingPhrases(t, language, pricing, (leaf) => `${NS}.${leaf}`);
}

/**
 * Any model in one line, from the language-neutral phrases of
 * src/lib/offerPricingDescribe.js — the band boundaries, the bound variants
 * and the order are the module's; the words come from `t` through `keyOf`
 * (the phrase's leaf key → a key path) and the numbers from the formatters.
 * describeOfferPricing is this with the `offer_pricing.*` keys for the two
 * volume models; the portal's offer editor adds its own keys for the three
 * single-figure models. Null for no model.
 */
export function formatOfferPricingPhrases(
  t: Translate,
  language: Language,
  pricing: OfferPricing | null | undefined,
  keyOf: (leaf: OfferPricingPhraseKey) => string
): string | null {
  const phrases = offerPricingPhrases(pricing);
  if (!phrases) return null;
  return phrases
    .map(({ key, values }) =>
      fillTemplate(t(keyOf(key)), {
        pct: values.pct !== undefined ? formatCommissionPct(values.pct, language) : null,
        kwh: values.kwh !== undefined ? formatInteger(values.kwh, language) : null,
        rate: values.rate !== undefined ? formatKwhRate(values.rate, language) : null,
        min: values.min !== undefined ? formatBound(values.min, language) : null,
        max: values.max !== undefined ? formatBound(values.max, language) : null,
      })
    )
    .join(OFFER_PRICING_PHRASE_SEPARATOR);
}

/** One row of a staffel's ladder (src/components/offers/StaffelLadder.tsx). */
export interface StaffelLadderRow {
  /** The band's kWh range: "tot 3.500 kWh", "3.500 – 5.000 kWh", "vanaf 8.000 kWh". */
  range: string;
  /** The band's %, as the row prints it: "22%". */
  pct: string;
  /** The band the surface's volume falls in — the one row drawn emphasised. */
  current: boolean;
}

/**
 * A staffel as its ladder, one row per band (staffelLadderBands): the range in
 * the page's words (`offer_pricing.ladder_*`) and number format, the % as the
 * row prints it, and the band `atKwh` falls in marked `current` (tierFor: the
 * boundary belongs to the band it opens). No `atKwh`: no band is current.
 */
export function staffelLadderRows(
  t: Translate,
  language: Language,
  tiers: readonly OfferPricingTier[],
  atKwh?: number | null
): StaffelLadderRow[] {
  const at =
    typeof atKwh === 'number' && Number.isFinite(atKwh) && atKwh > 0 && tiers.length > 0
      ? tierFor([...tiers], atKwh).fromKwh
      : null;
  return staffelLadderBands(tiers).map((band) => ({
    range: fillTemplate(t(`${NS}.${band.key}`), {
      from: formatInteger(band.from, language),
      to: band.to === null ? null : formatInteger(band.to, language),
    }),
    pct: `${formatCommissionPct(band.pct, language)}%`,
    current: at === band.from,
  }));
}

/**
 * The amber icon's title (and its accessible name) beside a staffel's or a
 * vastrecht offer's percentage: "Afwijkend verdienmodel: staffel — … {model}."
 * Null for a model without words here (the caller keeps its own three).
 */
export function offerPricingTooltip(
  t: Translate,
  language: Language,
  pricing: OfferPricing | null | undefined
): string | null {
  const kind = volumeModelKind(pricing);
  const model = describeOfferPricing(t, language, pricing);
  if (kind === null || model === null) return null;
  return fillTemplate(t(`table.deviating_model_${kind}`), { model });
}

/**
 * The overview card's percentage with its model named: "10% (staffel)" — a
 * staffel's lowest band —, "14% + vastrecht". `pct` is the formatted figure
 * with its "%" (the card's own format).
 */
export function overviewCommissionValue(t: Translate, kind: VolumeModelKind, pct: string): string {
  return fillTemplate(t(`${NS}.overview_${kind}`), { pct });
}

/** The overview card's title for the model: the card knows the kind, not the bands. */
export function overviewModelTooltip(t: Translate, kind: VolumeModelKind): string {
  return t(`${NS}.overview_${kind}_title`);
}

/** One extra line of a worked example: its label, its amount and how it enters the sum. */
export interface OfferBreakdownLine {
  label: string;
  /** Euros, never negative. */
  amount: number;
  /** '−' takes it off the yield, '+' gives it back (a cap that bit). */
  sign: '−' | '+';
}

export interface OfferBreakdownLines {
  /** The vastrecht per kWh, when the model charges one. */
  kwhFee: OfferBreakdownLine | null;
  /**
   * The yearly bound that applied: a cap gives back what commission +
   * vastrecht ran over it (+), a floor takes what they fell short of it (−).
   */
  clamp: OfferBreakdownLine | null;
}

/**
 * What the yearly bound changed, in cents: clampedTo − (commission + kwhFee),
 * from the breakdown's own printed cents — negative for a cap, positive for a
 * floor, 0 when neither applied. Read from the PRINTED lines, so the worked
 * example's lines add up to its net exactly (the cent chain, src/lib/earnings.ts).
 */
function clampCents(b: OfferEarningsBreakdown): number {
  if (b.clampedTo === null) return 0;
  return (
    Math.round(b.clampedTo * 100) - Math.round(b.commission * 100) - Math.round(b.kwhFee * 100)
  );
}

/**
 * The worked example's lines beyond the commission: "Vastrecht (2.500 kWh ×
 * € 0,0057)" − € 14,25 when the model charges one, and "Begrensd op € 99,99
 * per jaar" + € 95,20 / "Aangevuld tot het minimum van € 34,99 per jaar"
 * − € 0,82 when a yearly bound applied. The rate is the pricing's own.
 */
export function offerBreakdownLines(
  t: Translate,
  language: Language,
  b: OfferEarningsBreakdown,
  kwh: number,
  pricing: OfferPricing | null | undefined
): OfferBreakdownLines {
  const rate = pricing?.model === 'commission_plus_kwh_fee' ? pricing.eurPerKwh : null;
  const kwhFee: OfferBreakdownLine | null =
    b.kwhFee > 0 && rate !== null
      ? {
          label: fillTemplate(t(`${NS}.breakdown_kwh_fee`), {
            kwh: formatInteger(kwh, language),
            rate: formatKwhRate(rate, language),
          }),
          amount: b.kwhFee,
          sign: '−',
        }
      : null;
  const diff = clampCents(b);
  const clamp: OfferBreakdownLine | null =
    b.clampedTo !== null && diff !== 0
      ? {
          label: fillTemplate(t(diff < 0 ? `${NS}.breakdown_cap` : `${NS}.breakdown_floor`), {
            amount: formatBound(b.clampedTo, language),
          }),
          amount: Math.abs(diff) / 100,
          sign: diff < 0 ? '+' : '−',
        }
      : null;
  return { kwhFee, clamp };
}

/**
 * The chain behind the comparer's net figure, as its title: "832 ERE ×
 * € 0,4075 − € 5,09 − € 46,75 − € 14,25 = € 272,95", with the vastrecht and
 * the yearly bound in it when the model has them ("+ € 95,20 (max. € 99,99)").
 * For a flat offer the string the row always printed. Undefined when the
 * costs exceed the gross (a low volume, high service costs): the net is
 * floored at € 0, and "… − € 60,00 = € 0,00" would print a sum that does not
 * add up.
 */
export function offerBreakdownTitle(
  t: Translate,
  language: Language,
  b: OfferEarningsBreakdown | null
): string | undefined {
  if (b === null) return undefined;
  const diff = clampCents(b);
  const costCents = Math.round(b.commission * 100) + Math.round(b.kwhFee * 100) + diff;
  const addsUp =
    Math.round(b.gross * 100) -
      Math.round(b.market * 100) -
      costCents -
      Math.round(b.serviceCosts * 100) >=
    0;
  if (!addsUp) return undefined;
  const m = (n: number) => money2(n, language);
  const clampTerm =
    b.clampedTo !== null && diff !== 0
      ? ` ${diff < 0 ? '+' : '−'} ${m(Math.abs(diff) / 100)} (${fillTemplate(
          t(diff < 0 ? `${NS}.title_cap` : `${NS}.title_floor`),
          { amount: formatBound(b.clampedTo, language) }
        )})`
      : '';
  return `${formatInteger(b.eres, language)} ERE × ${formatErePrice(b.price, language)} − ${m(b.market)} − ${m(b.commission)}${
    b.kwhFee > 0 ? ` − ${m(b.kwhFee)}` : ''
  }${clampTerm}${b.serviceCosts > 0 ? ` − ${m(b.serviceCosts)}` : ''} = ${m(b.net)}`;
}

/** Every key this module can print — the coverage test holds both baselines to it. */
export const OFFER_PRICING_TEXT_KEYS: readonly string[] = [
  ...[
    'tier_until',
    'tier_above',
    'kwh_fee',
    'kwh_fee_min',
    'kwh_fee_max',
    'kwh_fee_min_max',
    'capped_min',
    'capped_max',
    'capped_min_max',
    'overview_tiered',
    'overview_kwh_fee',
    'overview_capped',
    'overview_tiered_title',
    'overview_kwh_fee_title',
    'overview_capped_title',
    'breakdown_kwh_fee',
    'breakdown_cap',
    'breakdown_floor',
    'title_cap',
    'title_floor',
    'ladder_until',
    'ladder_between',
    'ladder_from',
  ].map((leaf) => `${NS}.${leaf}`),
  'table.deviating_model_tiered',
  'table.deviating_model_kwh_fee',
  'table.deviating_model_capped',
];
